// SignalR transport for the game: one server stream carries seed + ticks +
// round events; commands run as parallel invocations on the same connection.
import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import type { HubConnection, ISubscription } from '@microsoft/signalr';
import type { MarketFrameDto, RoundStateDto, SessionStateDto } from './types';

const TICK_MS = 200;

export interface GameTransportHandlers {
  onFrame(frame: MarketFrameDto): void;
  onStreamDown(error?: unknown): void;
}

export class GameTransport {
  private readonly connection: HubConnection;
  private subscription: ISubscription<MarketFrameDto> | null = null;

  constructor(private readonly handlers: GameTransportHandlers) {
    this.connection = new HubConnectionBuilder()
      .withUrl('/hubs/game')
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();
    this.connection.onreconnected(() => this.subscribe());
    this.connection.onreconnecting(() => this.handlers.onStreamDown());
  }

  async start(): Promise<void> {
    await this.connection.start();
    this.subscribe();
  }

  /** (Re)attaches the market stream; the server answers with a fresh seed frame. */
  subscribe(): void {
    if (this.connection.state !== HubConnectionState.Connected) return;
    this.subscription?.dispose();
    this.subscription = this.connection.stream<MarketFrameDto>('StreamMarket', TICK_MS).subscribe({
      next: (frame) => this.handlers.onFrame(frame),
      error: (err) => this.handlers.onStreamDown(err),
      complete: () => this.handlers.onStreamDown(),
    });
  }

  pause(): void {
    this.subscription?.dispose();
    this.subscription = null;
  }

  get streaming(): boolean {
    return this.subscription !== null;
  }

  startRound(direction: 'buy' | 'sell'): Promise<RoundStateDto | null> {
    return this.connection.invoke<RoundStateDto | null>('StartRound', direction);
  }

  setDifficulty(difficulty: string): Promise<SessionStateDto> {
    return this.connection.invoke<SessionStateDto>('SetDifficulty', difficulty);
  }

  setMode(mode: string): Promise<SessionStateDto> {
    return this.connection.invoke<SessionStateDto>('SetMode', mode);
  }

  resetRun(): Promise<SessionStateDto> {
    return this.connection.invoke<SessionStateDto>('ResetRun');
  }
}
